import { type PropsOf } from '@emotion/react';
import { type SnackbarProps } from '@mui/material';
import {
  createContext,
  useState,
  useCallback,
  useMemo,
  type PropsWithChildren,
  type ReactNode,
} from 'react';
import { type FormattedMessage, type MessageDescriptor } from 'react-intl';

import Snackbar from './Snackbar';
import { SnackbarVariant, type SnackbarAction, type SnackbarPayload } from './types';

type SnackbarState = {
  snackbar: (payload: SnackbarPayload) => void;
  closeSnackbar: (delay?: number) => void;
  isSnackbarOpen: boolean;
};

export const SnackbarContext = createContext<SnackbarState | undefined>(undefined);

const SnackbarProvider = ({ children }: PropsWithChildren) => {
  const [open, setOpen] = useState(false);
  const [onCloseCallback, setOnCloseCallback] = useState<(() => void) | undefined>(undefined);
  const [message, setMessage] = useState<MessageDescriptor>({});
  const [messageTitle, setMessageTitle] = useState<MessageDescriptor | undefined>({});
  const [autoHide, setAutoHide] = useState(true);
  const [autoHideDuration, setAutoHideDuration] = useState<number | null>(null);
  const [action, setAction] = useState<SnackbarAction | undefined>();
  const [icon, setIcon] = useState<ReactNode | undefined>();
  const [variant, setVariant] = useState<SnackbarVariant>(SnackbarVariant.LIGHT);
  const [values, setValues] = useState<PropsOf<typeof FormattedMessage>['values']>({});
  const [anchorOrigin, setAnchorOrigin] = useState<SnackbarProps['anchorOrigin']>();
  const [sx, setSx] = useState<SnackbarProps['sx']>();
  const showSnackbar = useCallback(
    ({
      message,
      messageTitle,
      action,
      icon,
      autoHide = true,
      autoHideDuration = 6000,
      variant = SnackbarVariant.LIGHT,
      values = {},
      onCloseCallback,
      anchorOrigin = { vertical: 'bottom', horizontal: 'center' },
      sx = {},
    }: SnackbarPayload) => {
      setOnCloseCallback(() => onCloseCallback);
      setMessage(message);
      setMessageTitle(messageTitle);
      setAction(action);
      setIcon(icon);
      setVariant(variant);
      setOpen(true);
      setValues(values);
      setAutoHide(autoHide);
      setAutoHideDuration(autoHide ? autoHideDuration : null);
      setAnchorOrigin(anchorOrigin);
      setSx(sx);
    },
    [setMessage, setAction, setVariant, setOpen, setValues, setAutoHide, setAutoHideDuration],
  );

  const snackbar = useCallback(
    ({ message, messageTitle, action, autoHideDuration, ...other }: SnackbarPayload) => {
      showSnackbar({
        message,
        messageTitle,
        action,
        autoHideDuration,
        ...other,
      });
    },
    [showSnackbar],
  );

  const closeSnackbar = useCallback(
    (delay = 0) => {
      setTimeout(() => {
        setOpen(false);
        onCloseCallback?.();
      }, delay * 1000);
    },
    [onCloseCallback],
  );

  const contextValue = useMemo(
    () => ({
      snackbar,
      closeSnackbar,
      isSnackbarOpen: open,
    }),
    [snackbar, closeSnackbar, open],
  );

  return (
    <SnackbarContext.Provider value={contextValue}>
      {children}
      <Snackbar
        open={open}
        message={message}
        messageTitle={messageTitle}
        icon={icon}
        action={action}
        autoHide={autoHide}
        autoHideDuration={autoHideDuration}
        onClose={closeSnackbar}
        variant={variant}
        values={values}
        anchorOrigin={anchorOrigin}
        sx={sx}
      />
    </SnackbarContext.Provider>
  );
};

export default SnackbarProvider;
